import type { RegistryItem } from "shadcn/schema";

/**
 * The devices, each installed in one: its state, its engine and its panel,
 * to `components/audio-ui/devices/<name>/`. Each piece stays an item of its
 * own, for a panel over an engine of your own or an engine with no panel.
 */
const DEVICES = [
  "auto-pan",
  "channel-eq",
  "chorus",
  "compressor",
  "deesser",
  "delay",
  "distortion",
  "equalizer",
  "filter",
  "gate",
  "limiter",
  "mid-side",
  "multiband",
  "overdrive",
  "phaser",
  "pocket-synth",
  "redux",
  "reverb",
  "saturator",
  "transient",
  "utility",
  "wave-shaper",
];

const devices = DEVICES.map(
  (name): RegistryItem => ({
    categories: ["device"],
    description: `The ${name.replaceAll("-", " ")} device in one install: its state, its Web Audio engine and its panel, to components/audio-ui/devices/${name}/.`,
    files: [],
    name,
    registryDependencies: [
      `@audio-ui/${name}-device`,
      `@audio-ui/${name}-engine`,
      `@audio-ui/${name}-panel`,
    ],
    type: "registry:ui",
  })
);

/** How an envelope refuses a point: the styles' own keyframes, which an install has no other way to get. */
const ENVELOPE_SHAKE = {
  "@keyframes envelope-shake": {
    "0%": {
      "animation-timing-function": "cubic-bezier(0.22, 1, 0.36, 1)",
      translate: "0 0",
    },
    "28.57%": {
      "animation-timing-function": "cubic-bezier(0.22, 1, 0.36, 1)",
      translate: "6px 0",
    },
    "57.14%": {
      "animation-timing-function": "cubic-bezier(0.22, 1, 0.36, 1)",
      translate: "-6px 0",
    },
    "78.57%": {
      "animation-timing-function": "cubic-bezier(0.22, 1, 0.36, 1)",
      translate: "4px 0",
    },
    "100%": { translate: "0 0" },
  },
};

export const audio: RegistryItem[] = [
  {
    dependencies: ["@audio-ui/react"],
    description:
      "An editor for a breakpoint ramp, such as an ADSR shape or an automation curve.",
    files: [
      {
        path: "audio/elements/envelope.tsx",
        type: "registry:ui",
      },
    ],
    css: ENVELOPE_SHAKE,
    name: "envelope",
    type: "registry:ui",
  },
  {
    dependencies: ["@audio-ui/react"],
    description:
      "A track's automation over its lane in a timeline: an envelope along the whole arrangement, under the same zoom and scroll as the clips, whose line and points alone take the pointer.",
    files: [
      {
        path: "audio/elements/automation-envelope.tsx",
        type: "registry:ui",
      },
    ],
    css: ENVELOPE_SHAKE,
    name: "automation-envelope",
    type: "registry:ui",
  },
  {
    description:
      "A clip's recording drawn on a canvas, a column per pixel of the screen, as its clip plays it: looped, reversed, warped. It draws only what the view shows, and a zoom in the frame it happens.",
    files: [
      {
        path: "audio/elements/clip-waveform.tsx",
        type: "registry:ui",
      },
    ],
    name: "clip-waveform",
    registryDependencies: ["@audio-ui/arrangement"],
    type: "registry:ui",
  },
  {
    dependencies: ["@audio-ui/core", "@audio-ui/react"],
    description:
      "A clip of an arrangement on its track's lane: move it, trim and fade it, pick a fade's curve, rename it in place and act on it from its menu. A MIDI clip draws its notes across its body.",
    files: [
      {
        path: "audio/elements/arranged-clip.tsx",
        type: "registry:ui",
      },
      {
        path: "hooks/use-arranged-clips.ts",
        type: "registry:hook",
      },
    ],
    name: "arranged-clip",
    registryDependencies: [
      "@audio-ui/clip-waveform",
      "@audio-ui/timeline",
      "@audio-ui/arrangement",
      "@audio-ui/use-arrangement",
      "@shadcn/context-menu",
    ],
    type: "registry:ui",
  },
  {
    dependencies: ["@audio-ui/core"],
    description:
      "Locators and time-signature changes on a timeline's lane: press one to jump there, drag it or use the arrows to move it on the grid, rename or retype it in place, delete either. With the bars of a ruler through those changes, and a hook for the commands behind them.",
    files: [
      {
        path: "audio/elements/timeline-markers.tsx",
        type: "registry:ui",
      },
      {
        path: "hooks/use-timeline-markers.ts",
        type: "registry:hook",
      },
    ],
    name: "timeline-markers",
    registryDependencies: [
      "@audio-ui/timeline",
      "@audio-ui/use-arrangement",
      "@shadcn/context-menu",
    ],
    type: "registry:ui",
  },
  {
    dependencies: ["@audio-ui/core"],
    description:
      "A track's automation: a chooser of the parameter its lane shows, Volume or Pan, for its header, and that parameter's envelope over its lane, standing faint at the mix's value until it is drawn, with a hook for automation mode and the gains and pans moved by hand, which play their mix until enabled again.",
    files: [
      {
        path: "audio/elements/track-automation.tsx",
        type: "registry:ui",
      },
      {
        path: "hooks/use-track-automation.ts",
        type: "registry:hook",
      },
    ],
    name: "track-automation",
    registryDependencies: [
      "@audio-ui/automation-envelope",
      "@audio-ui/arrangement",
      "@audio-ui/use-arrangement",
      "@shadcn/button",
      "@shadcn/select",
    ],
    type: "registry:ui",
  },
  {
    dependencies: ["@audio-ui/react"],
    description:
      "A rotary control for continuous parameters, with pointer, wheel and keyboard input.",
    files: [
      {
        path: "audio/elements/knob.tsx",
        type: "registry:ui",
      },
    ],
    name: "knob",
    type: "registry:ui",
  },
  {
    dependencies: ["@audio-ui/react"],
    description: "A linear slider for gain and other continuous parameters.",
    files: [
      {
        path: "audio/elements/fader.tsx",
        type: "registry:ui",
      },
    ],
    name: "fader",
    type: "registry:ui",
  },
  {
    dependencies: ["@audio-ui/react"],
    description:
      "A two-dimensional control for two parameters at once, with pointer, wheel and keyboard input.",
    files: [
      {
        path: "audio/elements/xypad.tsx",
        type: "registry:ui",
      },
    ],
    name: "xypad",
    type: "registry:ui",
  },
  {
    dependencies: ["@audio-ui/react"],
    description:
      "A grid of drum pads struck by pointer, touch or keyboard, with velocity and choke groups.",
    files: [
      {
        path: "audio/elements/pad.tsx",
        type: "registry:ui",
      },
    ],
    name: "pad",
    type: "registry:ui",
  },
  {
    dependencies: ["@audio-ui/react"],
    description:
      "A piano keyboard for a MIDI pitch range, played with the pointer, touch or the keyboard.",
    files: [
      {
        path: "audio/elements/piano.tsx",
        type: "registry:ui",
      },
    ],
    name: "piano",
    type: "registry:ui",
  },
  {
    dependencies: ["@audio-ui/react"],
    description:
      "A note editor on a grid: draw, move, resize and select notes, with a playable keyboard and a velocity lane.",
    files: [
      {
        path: "audio/elements/piano-roll.tsx",
        type: "registry:ui",
      },
    ],
    name: "piano-roll",
    registryDependencies: ["@audio-ui/timeline"],
    type: "registry:ui",
  },
  {
    categories: ["piano"],
    dependencies: ["@audio-ui/core"],
    description:
      "A menu that picks a root and a scale and turns the scale on or off, in parts you place, beside items of your own.",
    files: [
      {
        path: "audio/elements/scale-menu.tsx",
        type: "registry:ui",
      },
    ],
    name: "scale-menu",
    registryDependencies: ["@shadcn/button", "@shadcn/dropdown-menu"],
    type: "registry:ui",
  },
  {
    categories: ["piano"],
    dependencies: ["@audio-ui/core"],
    description:
      "Finds notes by what they are: a pitch, ranges of velocity, length and chance, in or out of the scale, active or not, every few, or the rest. A form, or the form in a popover.",
    files: [
      {
        path: "audio/elements/note-find.tsx",
        type: "registry:ui",
      },
    ],
    name: "note-find",
    registryDependencies: [
      "@shadcn/button",
      "@shadcn/input",
      "@shadcn/popover",
      "@shadcn/select",
    ],
    type: "registry:ui",
  },
  {
    categories: ["piano"],
    dependencies: ["@audio-ui/core"],
    description:
      "A menu of commands over a piano roll's notes (split, join, quantize, legato, time commands, fit to scale, chance, velocity), with their keys: items and submenus you place, beside items of your own. The commands come as data in lib/note-edits, to run from keys or a toolbar too.",
    files: [
      {
        path: "audio/elements/note-edit-menu.tsx",
        type: "registry:ui",
      },
      {
        path: "lib/note-edits.ts",
        type: "registry:lib",
      },
    ],
    name: "note-edit-menu",
    registryDependencies: ["@shadcn/button", "@shadcn/dropdown-menu"],
    type: "registry:ui",
  },
  {
    dependencies: ["@audio-ui/react"],
    description:
      "The note and the pitch of a sound, live from the microphone, with a needle on a scale of cents.",
    files: [
      {
        path: "audio/elements/tuner.tsx",
        type: "registry:ui",
      },
    ],
    name: "tuner",
    registryDependencies: ["@audio-ui/use-pitch"],
    type: "registry:ui",
  },
  {
    categories: ["device", "knob"],
    dependencies: ["@audio-ui/core"],
    description:
      "A panel for any device: a knob per number and a toggle per switch, from its descriptors, controlled by a state a host owns.",
    files: [
      {
        path: "audio/panels/device-panel.tsx",
        type: "registry:ui",
      },
    ],
    name: "device-panel",
    registryDependencies: [
      "@audio-ui/knob",
      "@audio-ui/waveform-select",
      "@audio-ui/filter-type-select",
      "@shadcn/toggle",
      "@shadcn/select",
    ],
    type: "registry:ui",
  },
  {
    categories: ["device-chain"],
    description:
      "A track's devices in a row, each drawn by its own panel: moved by drag or by the arrow keys, folded, switched off and removed, and a device dropped from a library where it goes. Controlled, with no audio in it.",
    files: [
      {
        path: "audio/elements/device-chain.tsx",
        type: "registry:ui",
      },
    ],
    name: "device-chain",
    registryDependencies: [
      "@audio-ui/sortable-drag",
      "@shadcn/button",
      "@shadcn/context-menu",
      "@shadcn/toggle",
    ],
    type: "registry:ui",
  },
  {
    dependencies: ["@audio-ui/core"],
    description:
      "The face of a rack: eight macros, each with a menu that maps a parameter of a device inside or lets one go, its chains with their volume, switch and solo, and the picked chain's devices, which the host draws.",
    files: [
      {
        path: "audio/panels/rack-panel.tsx",
        type: "registry:ui",
      },
    ],
    name: "rack-panel",
    registryDependencies: [
      "@audio-ui/arrangement",
      "@audio-ui/knob",
      "@shadcn/button",
      "@shadcn/dropdown-menu",
      "@shadcn/slider",
      "@shadcn/toggle",
    ],
    type: "registry:ui",
  },
  {
    dependencies: ["@audio-ui/core", "@audio-ui/react"],
    description:
      "The face of a drum rack: eight macros, sixteen pads on a bank of the 128 notes, each named for its sound and played when struck, the picked pad's voice, tune, decay and mix, a recording dropped on a pad, and the picked pad's devices, which the host draws.",
    files: [
      {
        path: "audio/panels/drum-rack-panel.tsx",
        type: "registry:ui",
      },
    ],
    name: "drum-rack-panel",
    registryDependencies: [
      "@audio-ui/arrangement",
      "@audio-ui/knob",
      "@audio-ui/pad",
      "@audio-ui/rack-panel",
      "@shadcn/button",
      "@shadcn/toggle",
    ],
    type: "registry:ui",
  },
  {
    categories: ["device-chain"],
    description:
      "A library of devices by shelf, with a search: a device is added with Enter, a double-click or the Add button, or dragged to where it goes on a device chain.",
    files: [
      {
        path: "audio/elements/device-browser.tsx",
        type: "registry:ui",
      },
    ],
    name: "device-browser",
    registryDependencies: [
      "@audio-ui/device-chain",
      "@shadcn/button",
      "@shadcn/input-group",
    ],
    type: "registry:ui",
  },
  {
    dependencies: ["@audio-ui/core"],
    description:
      "Every device's panel by the device's stable id, with the width it is drawn at in a row: what draws a track's chain from the ids a project stores, with the face each device has on its own page.",
    files: [
      {
        path: "audio/device-panels.tsx",
        type: "registry:ui",
      },
    ],
    name: "device-panels",
    registryDependencies: [
      "@audio-ui/auto-pan-panel",
      "@audio-ui/channel-eq-panel",
      "@audio-ui/chorus-panel",
      "@audio-ui/compressor-panel",
      "@audio-ui/deesser-panel",
      "@audio-ui/delay-panel",
      "@audio-ui/distortion-panel",
      "@audio-ui/equalizer-panel",
      "@audio-ui/filter-panel",
      "@audio-ui/gate-panel",
      "@audio-ui/limiter-panel",
      "@audio-ui/mid-side-panel",
      "@audio-ui/multiband-panel",
      "@audio-ui/overdrive-panel",
      "@audio-ui/phaser-panel",
      "@audio-ui/pocket-synth-panel",
      "@audio-ui/redux-panel",
      "@audio-ui/reverb-panel",
      "@audio-ui/saturator-panel",
      "@audio-ui/transient-panel",
      "@audio-ui/utility-panel",
      "@audio-ui/wave-shaper-panel",
    ],
    type: "registry:ui",
  },
  {
    categories: ["device", "plot"],
    dependencies: ["@audio-ui/react"],
    description:
      "The face of the compressor device: the transfer curve it plays, with the threshold and the level going in on it, over the device's knobs.",
    files: [
      {
        path: "audio/panels/compressor-panel.tsx",
        target: "components/audio-ui/devices/compressor/panel.tsx",
        type: "registry:ui",
      },
    ],
    name: "compressor-panel",
    registryDependencies: [
      "@audio-ui/device-panel",
      "@audio-ui/plot",
      "@audio-ui/compressor-device",
    ],
    type: "registry:ui",
  },
  {
    categories: ["device", "synth", "xypad"],
    dependencies: ["@audio-ui/react", "@audio-ui/icons"],
    description:
      "The face of the Pocket Synth device: a pad that plays it, its waveform and filter, and its volume. Controlled, with the notes leaving through onNoteOn and onNoteOff.",
    files: [
      {
        path: "audio/panels/pocket-synth-panel.tsx",
        target: "components/audio-ui/devices/pocket-synth/panel.tsx",
        type: "registry:ui",
      },
    ],
    name: "pocket-synth-panel",
    registryDependencies: [
      "@audio-ui/device-panel",
      "@audio-ui/fader",
      "@audio-ui/waveform-select",
      "@audio-ui/xypad",
      "@audio-ui/pocket-synth-device",
      "@shadcn/toggle-group",
    ],
    type: "registry:ui",
  },
  {
    categories: ["device", "synth", "xypad"],
    dependencies: ["@audio-ui/react", "@audio-ui/icons"],
    description:
      "A row of buttons, one per waveform, each drawn as its shape: pick one. Fed by the options of a device's choice parameter.",
    files: [
      {
        path: "audio/elements/waveform-select.tsx",
        type: "registry:ui",
      },
    ],
    name: "waveform-select",
    registryDependencies: ["@shadcn/toggle-group"],
    type: "registry:ui",
  },
  {
    categories: ["device", "plot"],
    dependencies: ["@audio-ui/icons"],
    description:
      "A row of buttons, one per filter type, each drawn as the response it has: low-pass, high-pass, band-pass, notch, low and high cut, low and high shelf, bell. Fed by the options of a device's choice parameter.",
    files: [
      {
        path: "audio/elements/filter-type-select.tsx",
        type: "registry:ui",
      },
    ],
    name: "filter-type-select",
    registryDependencies: ["@shadcn/toggle-group"],
    type: "registry:ui",
  },
  {
    categories: ["device", "plot"],
    dependencies: ["@audio-ui/icons"],
    description:
      "A row of buttons, one per transfer curve, each drawn from the curve itself: you give it the function of each option, so the icon is what the device does and never drifts from it.",
    files: [
      {
        path: "audio/elements/curve-select.tsx",
        type: "registry:ui",
      },
    ],
    name: "curve-select",
    registryDependencies: ["@shadcn/toggle-group"],
    type: "registry:ui",
  },
  {
    categories: ["device", "hooks"],
    dependencies: ["@audio-ui/icons"],
    description:
      "A play and pause button for a demo signal: the icon follows the state, the name says what pressing it does.",
    files: [
      {
        path: "audio/elements/play-toggle.tsx",
        type: "registry:ui",
      },
    ],
    name: "play-toggle",
    registryDependencies: ["@shadcn/button"],
    type: "registry:ui",
  },
  {
    categories: ["device", "synth", "xypad"],
    dependencies: ["@audio-ui/react"],
    description:
      "The face of the Wave Shaper device: a pad that plays it, its waveform and its knobs. Controlled, with the notes leaving through onNoteOn and onNoteOff.",
    files: [
      {
        path: "audio/panels/wave-shaper-panel.tsx",
        target: "components/audio-ui/devices/wave-shaper/panel.tsx",
        type: "registry:ui",
      },
    ],
    name: "wave-shaper-panel",
    registryDependencies: [
      "@audio-ui/device-panel",
      "@audio-ui/waveform-select",
      "@audio-ui/xypad",
      "@audio-ui/wave-shaper-device",
    ],
    type: "registry:ui",
  },
  {
    categories: ["device", "plot", "knob"],
    dependencies: ["@audio-ui/react"],
    description:
      "The face of the delay device: the echoes it makes, one line each and fainter as they fade, with a handle on the first to drag its time and feedback, over the device's knobs.",
    files: [
      {
        path: "audio/panels/delay-panel.tsx",
        target: "components/audio-ui/devices/delay/panel.tsx",
        type: "registry:ui",
      },
    ],
    name: "delay-panel",
    registryDependencies: [
      "@audio-ui/device-panel",
      "@audio-ui/plot",
      "@audio-ui/delay-device",
    ],
    type: "registry:ui",
  },
  {
    categories: ["device", "plot", "knob"],
    dependencies: ["@audio-ui/react"],
    description:
      "The face of the reverb device: the tail it makes, its level falling to -60 dB, with a handle where it ends to drag the decay time, over the device knobs.",
    files: [
      {
        path: "audio/panels/reverb-panel.tsx",
        target: "components/audio-ui/devices/reverb/panel.tsx",
        type: "registry:ui",
      },
    ],
    name: "reverb-panel",
    registryDependencies: [
      "@audio-ui/device-panel",
      "@audio-ui/plot",
      "@audio-ui/reverb-device",
    ],
    type: "registry:ui",
  },
  {
    categories: ["device", "plot", "knob"],
    dependencies: ["@audio-ui/react"],
    description:
      "The face of the filter device: the curve it plays with a handle on the cutoff to drag its frequency and resonance, an optional live spectrum behind it, over the device knobs.",
    files: [
      {
        path: "audio/panels/filter-panel.tsx",
        target: "components/audio-ui/devices/filter/panel.tsx",
        type: "registry:ui",
      },
    ],
    name: "filter-panel",
    registryDependencies: [
      "@audio-ui/device-panel",
      "@audio-ui/plot",
      "@audio-ui/filter-device",
    ],
    type: "registry:ui",
  },
  {
    categories: ["device", "plot", "knob"],
    dependencies: ["@audio-ui/react"],
    description:
      "The face of the distortion device: its transfer curve against the straight line of a clean sound, over the device knobs.",
    files: [
      {
        path: "audio/panels/distortion-panel.tsx",
        target: "components/audio-ui/devices/distortion/panel.tsx",
        type: "registry:ui",
      },
    ],
    name: "distortion-panel",
    registryDependencies: [
      "@audio-ui/device-panel",
      "@audio-ui/plot",
      "@audio-ui/distortion-device",
    ],
    type: "registry:ui",
  },
  {
    categories: ["device", "plot", "knob"],
    dependencies: ["@audio-ui/react"],
    description:
      "The face of the chorus device: the delay time as the oscillator sweeps it over two seconds, over the device knobs.",
    files: [
      {
        path: "audio/panels/chorus-panel.tsx",
        target: "components/audio-ui/devices/chorus/panel.tsx",
        type: "registry:ui",
      },
    ],
    name: "chorus-panel",
    registryDependencies: [
      "@audio-ui/device-panel",
      "@audio-ui/plot",
      "@audio-ui/chorus-device",
    ],
    type: "registry:ui",
  },
  {
    categories: ["device", "knob"],
    dependencies: ["@audio-ui/react"],
    description:
      "The face of the utility device: its gain, pan and width as knobs and its mono and invert switches.",
    files: [
      {
        path: "audio/panels/utility-panel.tsx",
        target: "components/audio-ui/devices/utility/panel.tsx",
        type: "registry:ui",
      },
    ],
    name: "utility-panel",
    registryDependencies: [
      "@audio-ui/device-panel",
      "@audio-ui/utility-device",
    ],
    type: "registry:ui",
  },
  {
    categories: ["device", "plot", "knob"],
    dependencies: ["@audio-ui/react"],
    description:
      "The face of the limiter device: its transfer curve against the straight line of a clean sound with a handle for the ceiling and the input gain, over the device knobs.",
    files: [
      {
        path: "audio/panels/limiter-panel.tsx",
        target: "components/audio-ui/devices/limiter/panel.tsx",
        type: "registry:ui",
      },
    ],
    name: "limiter-panel",
    registryDependencies: [
      "@audio-ui/device-panel",
      "@audio-ui/plot",
      "@audio-ui/limiter-device",
    ],
    type: "registry:ui",
  },
  {
    categories: ["device", "plot", "knob"],
    dependencies: ["@audio-ui/react"],
    description:
      "The face of the gate device: its transfer curve against the straight line of a clean sound with a handle on the threshold, over the device knobs.",
    files: [
      {
        path: "audio/panels/gate-panel.tsx",
        target: "components/audio-ui/devices/gate/panel.tsx",
        type: "registry:ui",
      },
    ],
    name: "gate-panel",
    registryDependencies: [
      "@audio-ui/device-panel",
      "@audio-ui/plot",
      "@audio-ui/gate-device",
    ],
    type: "registry:ui",
  },
  {
    categories: ["device", "plot", "knob"],
    dependencies: ["@audio-ui/react"],
    description:
      "The face of the saturator device: its transfer curve against the straight line of a clean sound, over the device knobs and the choice of curve.",
    files: [
      {
        path: "audio/panels/saturator-panel.tsx",
        target: "components/audio-ui/devices/saturator/panel.tsx",
        type: "registry:ui",
      },
    ],
    name: "saturator-panel",
    registryDependencies: [
      "@audio-ui/device-panel",
      "@audio-ui/curve-select",
      "@audio-ui/plot",
      "@audio-ui/saturator-device",
    ],
    type: "registry:ui",
  },
  {
    categories: ["device", "plot", "knob"],
    dependencies: ["@audio-ui/react"],
    description:
      "The face of the phaser-flanger device: the sweep of its oscillator over two seconds, over the device knobs, with the ones the mode does not use drawn off.",
    files: [
      {
        path: "audio/panels/phaser-panel.tsx",
        target: "components/audio-ui/devices/phaser/panel.tsx",
        type: "registry:ui",
      },
    ],
    name: "phaser-panel",
    registryDependencies: [
      "@audio-ui/device-panel",
      "@audio-ui/plot",
      "@audio-ui/phaser-device",
    ],
    type: "registry:ui",
  },
  {
    categories: ["device", "plot", "knob"],
    dependencies: ["@audio-ui/react"],
    description:
      "The face of the auto pan device: what the oscillator does to the pan or the level over two seconds, over the device knobs.",
    files: [
      {
        path: "audio/panels/auto-pan-panel.tsx",
        target: "components/audio-ui/devices/auto-pan/panel.tsx",
        type: "registry:ui",
      },
    ],
    name: "auto-pan-panel",
    registryDependencies: [
      "@audio-ui/device-panel",
      "@audio-ui/plot",
      "@audio-ui/auto-pan-device",
    ],
    type: "registry:ui",
  },
  {
    categories: ["device", "plot", "knob"],
    dependencies: ["@audio-ui/react"],
    description:
      "The face of the channel EQ device: the curve it plays with a handle on the mid band and an optional live spectrum, over the shelf knobs and the high-pass switch.",
    files: [
      {
        path: "audio/panels/channel-eq-panel.tsx",
        target: "components/audio-ui/devices/channel-eq/panel.tsx",
        type: "registry:ui",
      },
    ],
    name: "channel-eq-panel",
    registryDependencies: [
      "@audio-ui/device-panel",
      "@audio-ui/plot",
      "@audio-ui/channel-eq-device",
    ],
    type: "registry:ui",
  },
  {
    categories: ["device", "plot", "knob"],
    dependencies: ["@audio-ui/react"],
    description:
      "The face of the overdrive device: the band that reaches the clip with a handle on its centre, over the device knobs.",
    files: [
      {
        path: "audio/panels/overdrive-panel.tsx",
        target: "components/audio-ui/devices/overdrive/panel.tsx",
        type: "registry:ui",
      },
    ],
    name: "overdrive-panel",
    registryDependencies: [
      "@audio-ui/device-panel",
      "@audio-ui/plot",
      "@audio-ui/overdrive-device",
    ],
    type: "registry:ui",
  },
  {
    categories: ["device", "plot", "knob"],
    dependencies: ["@audio-ui/react"],
    description:
      "The face of the de-esser device: what it does at each frequency with a handle at the frequency to drag across and up or down for the range, over the device knobs.",
    files: [
      {
        path: "audio/panels/deesser-panel.tsx",
        target: "components/audio-ui/devices/deesser/panel.tsx",
        type: "registry:ui",
      },
    ],
    name: "deesser-panel",
    registryDependencies: [
      "@audio-ui/device-panel",
      "@audio-ui/plot",
      "@audio-ui/deesser-device",
    ],
    type: "registry:ui",
  },
  {
    categories: ["device", "plot", "knob"],
    dependencies: ["@audio-ui/react"],
    description:
      "The face of the multiband compressor device: the three bands drawn against frequency with a handle on each split, over the knobs of each band.",
    files: [
      {
        path: "audio/panels/multiband-panel.tsx",
        target: "components/audio-ui/devices/multiband/panel.tsx",
        type: "registry:ui",
      },
    ],
    name: "multiband-panel",
    registryDependencies: [
      "@audio-ui/device-panel",
      "@audio-ui/plot",
      "@audio-ui/multiband-device",
    ],
    type: "registry:ui",
  },
  {
    categories: ["device", "plot", "knob"],
    dependencies: ["@audio-ui/react"],
    description:
      "The face of the transient shaper device: a square with the sustain across and the attack up, and a handle to drag to both at once, over the output knob.",
    files: [
      {
        path: "audio/panels/transient-panel.tsx",
        target: "components/audio-ui/devices/transient/panel.tsx",
        type: "registry:ui",
      },
    ],
    name: "transient-panel",
    registryDependencies: [
      "@audio-ui/device-panel",
      "@audio-ui/plot",
      "@audio-ui/transient-device",
    ],
    type: "registry:ui",
  },
  {
    categories: ["device", "plot", "knob"],
    dependencies: ["@audio-ui/react"],
    description:
      "The face of the mid/side device: a square with the side level across and the mid level up, and a handle to drag to both at once, over the bass mono knob.",
    files: [
      {
        path: "audio/panels/mid-side-panel.tsx",
        target: "components/audio-ui/devices/mid-side/panel.tsx",
        type: "registry:ui",
      },
    ],
    name: "mid-side-panel",
    registryDependencies: [
      "@audio-ui/device-panel",
      "@audio-ui/plot",
      "@audio-ui/mid-side-device",
    ],
    type: "registry:ui",
  },
  {
    categories: ["device", "plot", "knob"],
    dependencies: ["@audio-ui/react"],
    description:
      "The face of the redux device: the stairs the bits make against the straight line of a clean sound, over the device knobs.",
    files: [
      {
        path: "audio/panels/redux-panel.tsx",
        target: "components/audio-ui/devices/redux/panel.tsx",
        type: "registry:ui",
      },
    ],
    name: "redux-panel",
    registryDependencies: [
      "@audio-ui/device-panel",
      "@audio-ui/plot",
      "@audio-ui/redux-device",
    ],
    type: "registry:ui",
  },
  {
    categories: ["device", "plot", "knob"],
    dependencies: ["@audio-ui/react", "@audio-ui/core"],
    description:
      "The face of the equalizer device: the curve it plays with a handle on each band to drag its frequency and gain, an optional live spectrum behind it, over the device's knobs gathered by band.",
    files: [
      {
        path: "audio/panels/equalizer-panel.tsx",
        target: "components/audio-ui/devices/equalizer/panel.tsx",
        type: "registry:ui",
      },
    ],
    name: "equalizer-panel",
    registryDependencies: [
      "@audio-ui/device-panel",
      "@audio-ui/plot",
      "@audio-ui/equalizer-device",
      "@audio-ui/filter-type-select",
      "@shadcn/toggle",
    ],
    type: "registry:ui",
  },
  {
    dependencies: ["@audio-ui/react"],
    description:
      "The level of a sound across frequencies, live, on a log axis in hertz, with held peaks.",
    files: [
      {
        path: "audio/elements/spectrum-analyzer.tsx",
        type: "registry:ui",
      },
    ],
    name: "spectrum-analyzer",
    registryDependencies: ["@audio-ui/plot", "@audio-ui/use-spectrum"],
    type: "registry:ui",
  },
  {
    dependencies: ["@audio-ui/react", "@audio-ui/core"],
    description:
      "Curves and draggable points, for equalizers, filters and compressors.",
    files: [
      {
        path: "audio/elements/plot.tsx",
        type: "registry:ui",
      },
    ],
    name: "plot",
    type: "registry:ui",
  },
  {
    cssVars: {
      dark: {
        success: "oklch(0.696 0.17 162.48)",
        warning: "oklch(0.795 0.184 86.047)",
      },
      light: {
        success: "oklch(0.696 0.17 162.48)",
        warning: "oklch(0.795 0.184 86.047)",
      },
      theme: {
        "color-success": "var(--success)",
        "color-warning": "var(--warning)",
      },
    },
    dependencies: ["@audio-ui/react"],
    description:
      "A level meter in decibels for one or more channels, with peak hold.",
    files: [
      {
        path: "audio/elements/meter.tsx",
        type: "registry:ui",
      },
    ],
    name: "meter",
    type: "registry:ui",
  },
  {
    dependencies: [],
    description:
      "A track's level for its header: a thin vertical meter of what the engine meters on the track's channel, silent until an engine runs.",
    files: [
      {
        path: "audio/elements/track-level.tsx",
        type: "registry:ui",
      },
    ],
    name: "track-level",
    registryDependencies: ["@audio-ui/meter", "@audio-ui/use-audio-levels"],
    type: "registry:ui",
  },
  {
    dependencies: ["@audio-ui/core"],
    description:
      "A needle meter: a dial in dB that a live level moves, with the VU or the PPM ballistics of @audio-ui/core/meter. The needle turns outside React, so the dial does not re-render with the signal.",
    files: [
      {
        path: "audio/elements/needle-meter.tsx",
        type: "registry:ui",
      },
    ],
    name: "needle-meter",
    type: "registry:ui",
  },
  {
    dependencies: ["@audio-ui/react"],
    description:
      "Draws audio: a seekable waveform with regions and markers, a live waveform that scrolls with the signal, and a bar visualizer.",
    files: [
      {
        path: "audio/elements/waveform.tsx",
        type: "registry:ui",
      },
    ],
    name: "waveform",
    type: "registry:ui",
  },
  {
    categories: ["timeline", "waveform", "knob"],
    dependencies: ["@audio-ui/core", "@audio-ui/react"],
    description:
      "The clip view of an arrangement's audio clip: a strip with its switch and name that rolls the view up, then Clip (start, end), Loop (switch, position, length, ÷2, ×2) and Sample (Warp, Reverse, Crop, Gain, Transpose, speed) boxes in bars.beats.16ths, the last two foldable, beside its source on a timeline of its own (overview, bar ruler, the stretch it plays or loops to drag, warp markers on the waveform).",
    files: [
      {
        path: "audio/panels/clip-panel.tsx",
        type: "registry:ui",
      },
    ],
    name: "clip-panel",
    registryDependencies: [
      "@audio-ui/clip-waveform",
      "@audio-ui/parameter-field",
      "@audio-ui/clip-view",
      "@audio-ui/timeline",
    ],
    type: "registry:ui",
  },
  {
    categories: ["timeline"],
    description:
      "The frame of a clip's detail view: a strip along its top (the caret that rolls the view up to it, the clip's switch, its name to rename, a status, actions), boxes of its properties beside its editor (ClipBox, foldable to a strip), rows, small toggles, and position fields in bars.beats.16ths (parameter fields).",
    files: [
      {
        path: "audio/elements/clip-view.tsx",
        type: "registry:ui",
      },
    ],
    name: "clip-view",
    registryDependencies: ["@audio-ui/parameter-field", "@shadcn/button"],
    type: "registry:ui",
  },
  {
    categories: ["timeline", "piano"],
    dependencies: ["@audio-ui/core", "@audio-ui/react"],
    description:
      "The clip view of an arrangement's MIDI clip: a strip with its switch and name that rolls the view up, then Clip (start, end), Loop (switch, position, length, ÷2, ×2, Dupl. Loop) and Notes (transpose, invert, reverse, legato, ×2, ÷2, humanize, velocity ramps) boxes, the last two foldable, beside its piano roll (Draw, Fold, the loop brace on the ruler, keys, grid, a velocity or chance lane). Notes are drawn a grid step at a time.",
    files: [
      {
        path: "audio/panels/midi-clip-panel.tsx",
        type: "registry:ui",
      },
    ],
    name: "midi-clip-panel",
    registryDependencies: [
      "@audio-ui/clip-view",
      "@audio-ui/piano-roll",
      "@audio-ui/timeline",
      "@shadcn/button",
    ],
    type: "registry:ui",
  },
  {
    dependencies: ["@audio-ui/core", "@audio-ui/react", "@audio-ui/icons"],
    description:
      "A zoomable timeline for arrangement views and editors, with a ruler that seeks, lanes and a playhead.",
    files: [
      {
        path: "audio/elements/timeline.tsx",
        type: "registry:ui",
      },
    ],
    name: "timeline",
    registryDependencies: [
      "@audio-ui/arrangement",
      "@audio-ui/fader",
      "@audio-ui/knob",
      "@audio-ui/led",
      "@shadcn/button",
      "@shadcn/button-group",
      "@shadcn/context-menu",
      "@shadcn/dropdown-menu",
      "@shadcn/toggle-group",
    ],
    type: "registry:ui",
  },
  {
    dependencies: ["@base-ui/react"],
    description:
      "A tempo field on its own: type it, step it with the arrows, or drag its unit to scrub the value.",
    files: [
      {
        path: "audio/elements/bpm-control.tsx",
        type: "registry:ui",
      },
    ],
    name: "bpm-control",
    registryDependencies: ["@audio-ui/parameter-field"],
    type: "registry:ui",
  },
  {
    dependencies: ["@base-ui/react", "class-variance-authority"],
    description:
      "A parameter's value as a field: type it, step it with the arrows (Shift for large steps, Alt for small), or drag its label or unit to scrub it, sideways or up and down, with a unit, precision and steppers.",
    files: [
      {
        path: "audio/elements/parameter-field.tsx",
        type: "registry:ui",
      },
    ],
    name: "parameter-field",
    registryDependencies: ["@shadcn/input-group"],
    type: "registry:ui",
  },
  {
    dependencies: ["class-variance-authority"],
    description:
      "A status light that glows in its tone when lit and stays a dim dot when off, blinking for an armed take: recording, signal, mute.",
    files: [
      {
        path: "audio/elements/led.tsx",
        type: "registry:ui",
      },
    ],
    name: "led",
    type: "registry:ui",
  },
  {
    dependencies: ["class-variance-authority"],
    description:
      "A small screen, as on a synth: the foreground as its glass and the background as lit figures, holding a reading under a caption, in light and dark alike.",
    files: [
      {
        path: "audio/elements/lcd.tsx",
        type: "registry:ui",
      },
    ],
    name: "lcd",
    type: "registry:ui",
  },
  {
    dependencies: ["@audio-ui/core", "class-variance-authority"],
    description:
      "An audio value as it reads, in steady monospace figures: tempo, level in dB, frequency in Hz or kHz, pan, percent, semitones, time.",
    files: [
      {
        path: "audio/elements/value-display.tsx",
        type: "registry:ui",
      },
    ],
    name: "value-display",
    type: "registry:ui",
  },
  {
    dependencies: ["@base-ui/react", "class-variance-authority"],
    description:
      "A rocker switch, as on a mixer or a synth: two sides, the one pressed down sunk into the panel, upright or laid down, with a switch's role and keyboard.",
    files: [
      {
        path: "audio/elements/rocker-switch.tsx",
        type: "registry:ui",
      },
    ],
    name: "rocker-switch",
    type: "registry:ui",
  },
  {
    description:
      "A button that connects the page's MIDI input, with the device's name beside it. Renders nothing when the browser has no Web MIDI.",
    files: [
      {
        path: "audio/elements/midi-connect-control.tsx",
        type: "registry:ui",
      },
    ],
    name: "midi-connect-control",
    registryDependencies: [
      "@audio-ui/use-midi-input",
      "@shadcn/button",
      "@shadcn/button-group",
    ],
    type: "registry:ui",
  },
  {
    dependencies: ["@audio-ui/react", "@audio-ui/icons"],
    description:
      "A step grid: press a step, or drag across several, to draw a pattern.",
    files: [
      {
        path: "audio/elements/sequencer.tsx",
        type: "registry:ui",
      },
      {
        path: "components/sequencer/sequencer-demo.tsx",
        target: "components/audio-ui/sequencer-default.tsx",
        type: "registry:component",
      },
    ],
    name: "sequencer",
    registryDependencies: [
      "@audio-ui/use-master-bus",
      "@audio-ui/use-transport-clock",
      "@audio-ui/web-audio",
      "@audio-ui/drum-synthesis",
      "@shadcn/button",
    ],
    type: "registry:ui",
  },
  {
    dependencies: ["@audio-ui/react", "@audio-ui/icons"],
    description:
      "A compact player: play and pause, the position, a seek bar and the length.",
    files: [
      {
        path: "audio/elements/scrubber.tsx",
        type: "registry:ui",
      },
      {
        path: "components/scrubber/scrubber-demo.tsx",
        target: "components/audio-ui/scrubber-default.tsx",
        type: "registry:component",
      },
    ],
    name: "scrubber",
    registryDependencies: ["@shadcn/button"],
    type: "registry:ui",
  },
  {
    description:
      "DAW transport controls: play, pause and stop, the position in bars, beats and sixteenths, loop and metronome toggles, tempo and time signature.",
    files: [
      {
        path: "audio/elements/transport.tsx",
        type: "registry:ui",
      },
      {
        path: "components/transport/transport-demo.tsx",
        target: "components/audio-ui/transport-default.tsx",
        type: "registry:component",
      },
    ],
    name: "transport",
    dependencies: ["@audio-ui/react", "@audio-ui/core", "@audio-ui/icons"],
    registryDependencies: [
      "@audio-ui/bpm-control",
      "@shadcn/button",
      "@shadcn/button-group",
      "@shadcn/dropdown-menu",
      "@shadcn/select",
    ],
    type: "registry:ui",
  },
  ...devices,
  {
    description:
      "The parts of an arrangement editor in one install: the timeline, its zoom and grid, the arrangement's state, its clips, markers and automation, track reordering and MIDI playback. block-timeline wires them into a screen.",
    files: [],
    name: "arrangement-editor",
    registryDependencies: [
      "@audio-ui/timeline",
      "@audio-ui/arrangement",
      "@audio-ui/use-arrangement",
      "@audio-ui/arranged-clip",
      "@audio-ui/timeline-markers",
      "@audio-ui/track-automation",
      "@audio-ui/use-track-reorder",
      "@audio-ui/use-midi-playback",
      "@audio-ui/use-marquee-selection",
      "@audio-ui/use-timeline-zoom",
      "@audio-ui/use-timeline-grid",
      "@audio-ui/use-time-selection",
      "@audio-ui/use-timeline-cursor",
    ],
    type: "registry:ui",
  },
];
